<template>
  <v-card
    outlined
    class="rounded-lg"
    :class="{
      error: outsource.end,
      warning: !outsource.end
    }"
  >
    <v-card flat>
      <v-card-title>
        <v-icon class="mr-2">mdi-account-clock</v-icon>
        Preemptive Outsource Job
        <v-spacer />
        <v-chip :color="outsource.end ? 'error' : 'warning'">
          {{ outsource.end ? "Canceled" : "Pending" }}
        </v-chip>
        <v-btn v-if="!outsource.end" @click="edit = !edit" icon class="ml-2">
          <v-icon v-if="edit" color="warning">mdi-lock-open</v-icon>
          <v-icon v-if="!edit" color="warning">mdi-lock</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div v-if="!edit">
          <v-row>
            <v-col>
              Outsource To
              <br />
              <h3>
                {{ outsource.partner }}
              </h3>
            </v-col>
            <v-col>
              Outsource Type
              <br />
              <h3 class="text-capitalize">
                {{ outsourceJobType }}
              </h3>
            </v-col>
          </v-row>
        </div>
        <v-form
          ref="preemptiveOutsourceForm"
          lazy-validation
          v-if="edit && !outsource.end"
        >
          <v-autocomplete
            v-model="outsource.partner_id"
            :rules="[partnerIDValidation]"
            class="rounded-lg"
            outlined
            dense
            :items="partnerItems"
            label="Partner"
          />
          <v-text-field
            v-model="dueTime"
            :rules="[dueTimeValidation]"
            class="rounded-lg"
            outlined
            dense
            type="datetime-local"
            label="Due Time"
          />
          <v-autocomplete
            v-model="outsourceJobType"
            :rules="[outsourceJobTypeValidation]"
            :items="outsourceJobTypes"
            class="rounded-lg"
            outlined
            dense
            label="Job Type"
          />
          <v-autocomplete
            v-if="outsourceJobType === 'physical'"
            v-model="returningBranch"
            :rules="[returningbranchValidation]"
            :items="branchItems"
            class="rounded-lg"
            outlined
            dense
            label="Returning Branch"
          />
          <v-text-field
            class="rounded-lg"
            v-model="additionalRecipients"
            outlined
            dense
            label="Additional Recipients"
          />
          <v-textarea
            v-model="note"
            :rules="[noteValidation]"
            class="rounded-lg"
            outlined
            dense
            label="Note"
          />
        </v-form>
        <v-row v-if="edit">
          <v-col class="text-right">
            <v-btn
              :disabled="submitting"
              class="error rounded-lg mr-3"
              @click="cancelPreemptiveOutsource()"
            >
              <v-icon class="mr-2">mdi-stop</v-icon>
              End
            </v-btn>

            <v-btn
              :disabled="submitting"
              class="success rounded-lg"
              @click="startPreemptiveOutsource()"
            >
              <v-icon class="mr-2">mdi-send</v-icon>
              Start
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DateTime } from "luxon";

export default {
  name: "PreemptiveJobCard",

  props: ["outsource"],

  data() {
    return {
      edit: false,
      submitting: false,
      dueTime: null,
      additionalRecipients: null,
      note: null,
      outsourceJobType: this.outsource.upload_not_required
        ? "physical"
        : "digital",
      outsourceJobTypes: [
        { text: "Digital Job", value: "digital" },
        { text: "Physical Job", value: "physical" }
      ]
    };
  },

  computed: {
    ...mapGetters(["kase", "collection", "branches", "partners"]),

    dueTimeWithZone() {
      if (!this.dueTime) {
        return;
      }
      return DateTime.fromISO(this.dueTime).toISO();
    },

    returningBranch: {
      get() {
        return this.collection.fulfillment_branch_id;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.collection.fulfillment_branch_id = value;
      }
    },

    partnerItems() {
      return this.partners.map((partner) => {
        return {
          text: partner.name,
          value: partner.id
        };
      });
    },

    branchItems() {
      return this.branches.map((branch) => {
        return {
          text: branch.name,
          value: branch.id
        };
      });
    }
  },

  methods: {
    ...mapActions(["startLoading", "stopLoading", "triggerReload"]),

    async startPreemptiveOutsource() {
      if (!this.kase.in_design) {
        if (
          !confirm(
            "This case is not in design - if you start this outsource job, the case will be moved back to the design system. Are you sure this is okay?"
          )
        ) {
          return;
        }
      }
      if (!this.$refs.preemptiveOutsourceForm.validate()) {
        return;
      }
      this.submitting = true;
      this.startLoading();
      this.$axios
        .post(`/api/v2/case/${this.kase.case_id}/`, {
          action: "start_outsource",
          outsource_id: this.outsource.id,
          partner_id: this.outsource.partner_id,
          due_time: this.dueTimeWithZone,
          additional_recipients: this.additionalRecipients,
          job_type: this.outsourceJobType,
          returning_branch_id: this.returningBranch,
          note: this.note
        })
        .then(() => {
          this.triggerReload();
          this.stopLoading();
          this.submitting = false;
        });
    },

    async cancelPreemptiveOutsource() {
      if (!confirm("Are you sure?")) {
        return;
      }
      this.submitting = true;
      this.startLoading();
      this.$axios
        .post(`/api/v2/case/${this.kase.case_id}/`, {
          action: "end_outsource",
          outsource_id: this.outsource.id
        })
        .then(() => {
          this.triggerReload();
          this.stopLoading();
          this.submitting = false;
        });
    },

    partnerIDValidation() {
      if (!this.outsource.partner_id) {
        return "Must be selected";
      }
      return true;
    },

    outsourceJobTypeValidation() {
      if (!this.outsourceJobType) {
        return "Must be selected";
      }
      return true;
    },

    returningbranchValidation() {
      if (this.outsourceJobType === "physical" && !this.returningBranch) {
        return "Must be selected";
      }
      return true;
    },

    dueTimeValidation() {
      if (!this.dueTime) {
        return "Must be selected";
      }
      if (DateTime.now() > new Date(this.dueTime)) {
        return "Cannot be in the past";
      }
      return true;
    },

    noteValidation() {
      if (!this.note) {
        return "Must be provided";
      }
      return true;
    }
  }
};
</script>
